<script setup lang="ts">
import { useWindowSize } from "@vueuse/core";
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import { computed, nextTick, onMounted, provide, reactive, ref, watch } from "vue";
import { SINGLE_MODIFIERS_GROUP_ID } from "#/constants/modifiers";
import { useCardStore } from "#/store/card";
import { useDecorationStore } from "#/store/decoration";
import { useMainStore } from "#/store/main";
import { useModulesStore } from "#/store/modules";
import { useThemeStore } from "#/store/theme";
import type { IGroupModifierSelected, IGroupModifiers, IProduct, ISelectModifiers } from "#/types/iCardProduct";
import { BottomSheet } from "#/ui/bottomSheet";
import { getRegularModifierGroups, isSwiperModifierGroup, preserveSwiperGroupSelection, toSelectedGroup } from "#/utils/customRenderModifiers";
import { getPaidModifiersTotal } from "#/utils/freeModifiers";
import { formatPrice } from "#/utils/i18next";
import { applySelectedSizeToModifier, getModifierPriceForDishSize, scrollToRequiredEmpty } from "#/utils/syncModifierWithSize";
import LiquidModifiersBar from "./LiquidModifiersBar.vue";
import LiquidModifiersGroup from "./LiquidModifiersGroup.vue";
import LiquidModifiersPanel from "./LiquidModifiersPanel.vue";
import { liquidModifiersKey } from "./liquidModifiersContext";

const props = withDefaults(defineProps<{ inline?: boolean; decoration?: string; price?: string }>(), { inline: false, decoration: undefined, price: "" });

const emit = defineEmits<(e: "apply") => void>();

const { t } = useTranslation();

const cardStore = useCardStore();
const themeStore = useThemeStore();
const mainStore = useMainStore();
const modulesStore = useModulesStore();
const { stateCard } = storeToRefs(cardStore);
const decor = useDecorationStore().screen(props.decoration);
const isPanel = computed(() => !props.inline && decor.variant("modifiers", "sheet") === "panel");

const localCounts = reactive<Record<string, number>>({});
const localMarkingCodes = reactive<Record<string, string[]>>({});

let _isHydratingFromStore = false;
let _hasHydratedFromStore = false;

const isMarkingGated = (modifier: ISelectModifiers): boolean =>
	!!modifier.is_marked && !!themeStore.data?.retail_mode && typeof mainStore.modifierMarkRequester === "function";

const requestMarks = async (modifier: ISelectModifiers, count: number): Promise<string[] | null> => {
	const codes: string[] = [];
	for (let i = 0; i < count; i++) {
		const code = await mainStore.modifierMarkRequester?.(modifier, [...codes, ...Object.values(localMarkingCodes).flat()]);
		if (code == null) return null;
		codes.push(code);
	}
	return codes;
};

const clearCodes = (key: string) => {
	delete localMarkingCodes[key];
};

const groups = computed<IGroupModifiers[]>(() => getRegularModifierGroups(cardStore.stateCard?.groupModifiers));
const activeGroup = computed(() => groups.value.find((group) => group.id === stateCard.value?.groupCurrentOpen?.id) ?? null);

const openGroup = (group: IGroupModifiers | null | undefined) => {
	if (!group || !cardStore.stateCard) return;
	const index = cardStore.stateCard.groupModifiers.findIndex((item) => item.id === group.id);
	if (index === -1) return;
	cardStore.stateCard.groupCurrentOpen = { ...group, index };
};

const collapseGroups = () => {
	if (cardStore.stateCard) cardStore.stateCard.groupCurrentOpen = null;
};

const toggleGroup = (group: IGroupModifiers) => {
	if (group.id === activeGroup.value?.id) collapseGroups();
	else openGroup(group);
};

const inlineRef = ref<HTMLElement | null>(null);
const inlineRows = ref(2);
const { width: windowWidth, height: windowHeight } = useWindowSize();

const getScrollParent = (element: HTMLElement | null) => {
	let node = element?.parentElement ?? null;
	while (node) {
		const { overflowY } = getComputedStyle(node);
		if (overflowY === "auto" || overflowY === "scroll") return node;
		node = node.parentElement;
	}
	return null;
};

const fitInlineRows = async () => {
	if (!props.inline) return;
	inlineRows.value = 2;
	await nextTick();
	const scroller = getScrollParent(inlineRef.value);
	if (scroller && scroller.scrollHeight > scroller.clientHeight + 1) inlineRows.value = 1;
};

const isOpen = computed({
	get: () => !!cardStore.stateCard?.isSectionModifier,
	set: (value: boolean) => {
		if (cardStore.stateCard) cardStore.stateCard.isSectionModifier = value;
	},
});

const totalExtra = computed(() => {
	let sum = 0;
	for (const group of groups.value) {
		const selected = group.group_modifiers.map((modifier) => ({
			...modifier,
			amount: getAmount(modifier, group),
			price: getModifierPrice(modifier),
		}));
		sum += getPaidModifiersTotal(group.free_quantity, selected);
	}
	return sum;
});

const getTotalCapacity = (group: IGroupModifiers): number => {
	const openCard = cardStore.stateCard?.openCard as IProduct | null;
	if (!openCard) return 0;
	const groupIndexInGm = groups.value.findIndex((g) => g.id === group.id);
	if (groupIndexInGm === -1) return 0;

	const firstModifiers = openCard.modifiers ?? [];

	if (group.id === SINGLE_MODIFIERS_GROUP_ID && firstModifiers.length > 0) {
		return firstModifiers.reduce((sum, modifier) => sum + (modifier.max_amount ?? 0), 0);
	}
	return groups.value[groupIndexInGm].max_amount;
};

const isSupportsModifierQuantity = (modifier: ISelectModifiers, group: IGroupModifiers): boolean => {
	if (group.max_amount > 1) return true;
	if (modifier.max_amount > 1) return true;
	if (group.id === SINGLE_MODIFIERS_GROUP_ID && modifier.max_amount <= 1) return true;
	return false;
};

const isQuantitySupplementsGroup = (group: IGroupModifiers): boolean =>
	group.id === SINGLE_MODIFIERS_GROUP_ID && group.group_modifiers.some((modifier) => isSupportsModifierQuantity(modifier, group));

const allowsMultipleTypesInGroup = (group: IGroupModifiers): boolean => group.max_amount > 1 || isQuantitySupplementsGroup(group);

const shouldShowCounter = (modifier: ISelectModifiers, group: IGroupModifiers): boolean =>
	isSupportsModifierQuantity(modifier, group) && modifier.max_amount !== 1;

const isHiddenModifier = (modifier: ISelectModifiers, group: IGroupModifiers): boolean =>
	modifier.default_amount === 0 && group.id === SINGLE_MODIFIERS_GROUP_ID && modifier.min_amount === 0 && modifier.max_amount === 0;

const getKey = (groupId: string, modId: string) => `${groupId}:${modId}`;

const getAmount = (modifier: ISelectModifiers, group: IGroupModifiers): number => localCounts[getKey(group.id, modifier.id)] ?? 0;

const sumInGroup = (group: IGroupModifiers): number => group.group_modifiers.reduce((sum, modifier) => sum + getAmount(modifier, group), 0);

const isGroupSatisfied = (group: IGroupModifiers): boolean => {
	if ((group.required || group.min_amount > 0) && sumInGroup(group) < group.min_amount) return false;
	if (!props.inline) return true;
	return group.group_modifiers.every((modifier) => !(modifier.min_amount > 0) || getAmount(modifier, group) >= modifier.min_amount);
};

const getModifierPrice = (modifier: ISelectModifiers): number => getModifierPriceForDishSize(modifier, cardStore.stateCard?.selectedSize);

const modifierPerItemCap = (modifier: ISelectModifiers, group: IGroupModifiers): number => {
	if (modifier.max_amount === 0) return Infinity;
	if (modifier.max_amount > 0) return modifier.max_amount;
	const groupMax = group.max_amount;
	return groupMax > 0 ? groupMax : Infinity;
};

const getInitialAmount = (modifier: ISelectModifiers) => (modifier.min_amount > 0 ? modifier.min_amount : 1);

const canAddModifier = (modifier: ISelectModifiers, group: IGroupModifiers): boolean => {
	const initial = getInitialAmount(modifier);
	if (initial > modifierPerItemCap(modifier, group)) return false;
	return sumInGroup(group) + initial <= getTotalCapacity(group);
};

const canIncrementModifier = (modifier: ISelectModifiers, group: IGroupModifiers): boolean => {
	const current = getAmount(modifier, group);
	const perModifier = modifierPerItemCap(modifier, group);
	if (current >= perModifier) return false;
	return sumInGroup(group) + 1 <= getTotalCapacity(group);
};

const selectModifier = async (modifier: ISelectModifiers, group: IGroupModifiers) => {
	const key = getKey(group.id, modifier.id);
	const current = localCounts[key] ?? 0;

	/** Радио: один тип в группе (добавки без количества или обычная группа с max_amount === 1). */
	if (!allowsMultipleTypesInGroup(group)) {
		const wasSelected = current > 0;
		if (wasSelected) {
			for (const other of group.group_modifiers) {
				const otherKey = getKey(group.id, other.id);
				delete localCounts[otherKey];
				clearCodes(otherKey);
			}
			return false;
		}
		if (isMarkingGated(modifier)) {
			const codes = await requestMarks(modifier, 1);
			if (!codes) return false;
			localMarkingCodes[key] = codes;
		}
		for (const other of group.group_modifiers) {
			const otherKey = getKey(group.id, other.id);
			if (otherKey === key) continue;
			delete localCounts[otherKey];
			clearCodes(otherKey);
		}
		localCounts[key] = 1;
		return true;
	}

	/** Мульти + количество: новый тип дополняет выбор, не сбрасывает соседние. */
	if (current === 0) {
		if (!canAddModifier(modifier, group)) {
			if (modifier.min_amount > 0) modulesStore.showNotification(t(`card.error.modifier-min-amount`, { amount: modifier.min_amount }));
			return false;
		}
		const initial = getInitialAmount(modifier);
		if (isMarkingGated(modifier)) {
			const codes = await requestMarks(modifier, initial);
			if (!codes) return false;
			localMarkingCodes[key] = codes;
		}
		localCounts[key] = initial;
		return true;
	}

	/** Для количественных модификаторов — клик делает +1 или снимает выбор. */
	if (isSupportsModifierQuantity(modifier, group)) {
		if (canIncrementModifier(modifier, group)) {
			if (isMarkingGated(modifier)) {
				const codes = await requestMarks(modifier, 1);
				if (!codes) return false;
				const markingCodes = localMarkingCodes[key] ?? [];
				markingCodes.push(...codes);
				localMarkingCodes[key] = markingCodes;
			}
			localCounts[key] = current + 1;
			return true;
		}
		delete localCounts[key];
		clearCodes(key);
		return false;
	}

	const floor = modifier.min_amount > 0 ? modifier.min_amount : 0;
	if (floor > 0 && current <= floor) return false;
	delete localCounts[key];
	clearCodes(key);
	return false;
};

const incrementModifier = async (modifier: ISelectModifiers, group: IGroupModifiers) => {
	const key = getKey(group.id, modifier.id);
	const current = localCounts[key] ?? 0;
	if (!canIncrementModifier(modifier, group)) return false;
	if (isMarkingGated(modifier)) {
		const codes = await requestMarks(modifier, 1);
		if (!codes) return false;
		const markingCodes = localMarkingCodes[key] ?? [];
		markingCodes.push(...codes);
		localMarkingCodes[key] = markingCodes;
	}
	localCounts[key] = current + 1;
	return true;
};

const removeModifier = (modifier: ISelectModifiers, group: IGroupModifiers) => {
	const key = getKey(group.id, modifier.id);
	delete localCounts[key];
	clearCodes(key);
};

const decrementModifier = (modifier: ISelectModifiers, group: IGroupModifiers) => {
	const key = getKey(group.id, modifier.id);
	const current = localCounts[key] ?? 0;
	const floor = modifier.min_amount > 0 ? modifier.min_amount : 0;
	if (current <= floor) {
		if (props.inline && current > 0) removeModifier(modifier, group);
		return;
	}
	if (localMarkingCodes[key]?.length) localMarkingCodes[key].pop();
	if (current - 1 <= 0 && floor === 0) {
		delete localCounts[key];
		clearCodes(key);
	} else localCounts[key] = current - 1;
};

const hydrateLocalFromStore = () => {
	const stateCard = cardStore.stateCard;
	if (!stateCard?.openCard) {
		_hasHydratedFromStore = false;
		return;
	}
	_isHydratingFromStore = true;
	const next: Record<string, number> = {};
	const _groupModifiers = stateCard.groupModifiers ?? [];
	const _groupModifiersSelected = stateCard.groupModifiersSelected ?? [];
	for (let i = 0; i < _groupModifiers.length; i++) {
		const group = _groupModifiers[i];
		if (isSwiperModifierGroup(group)) continue;
		const modifiers = _groupModifiersSelected[i]?.modifiers ?? [];
		const byId = new Map(modifiers.map((modifier) => [modifier.id, modifier.amount ?? 0]));
		const codesById = new Map(modifiers.map((modifier) => [modifier.id, modifier.marking_codes ?? null]));
		for (const modifier of group.group_modifiers) {
			const amount = byId.get(modifier.id) ?? 0;
			if (amount > 0) {
				const key = getKey(group.id, modifier.id);
				next[key] = amount;
				const codes = codesById.get(modifier.id);
				if (Array.isArray(codes) && codes.length) localMarkingCodes[key] = [...codes];
				else clearCodes(key);
			}
		}
	}
	for (const key of Object.keys(localCounts)) if (next[key] === undefined) delete localCounts[key];
	for (const key of Object.keys(next)) if (localCounts[key] !== next[key]) localCounts[key] = next[key];
	_isHydratingFromStore = false;
	_hasHydratedFromStore = true;
};

const syncOnChange = () => {
	if (_isHydratingFromStore) return;
	const stateCard = cardStore.stateCard;
	if (!stateCard?.openCard || (!props.inline && !stateCard.isSectionModifier)) return;
	if (props.inline && !_hasHydratedFromStore) return;
	stateCard.groupModifiersSelected = syncStoreFromLocal();
};

const syncStoreFromLocal = (): IGroupModifierSelected[] => {
	const stateCard = cardStore.stateCard;
	if (!stateCard) return [];
	return (stateCard.groupModifiers ?? []).map((group, index) => {
		const preserved = preserveSwiperGroupSelection(group, index, stateCard.groupModifiersSelected);
		if (preserved) return preserved;

		const modifiers: ISelectModifiers[] = [];
		for (const modifier of group.group_modifiers) {
			const amount = getAmount(modifier, group);
			if (amount > 0) {
				const key = getKey(group.id, modifier.id);
				const codes = localMarkingCodes[key];
				const withCodes: ISelectModifiers = modifier.is_marked && codes?.length ? { ...modifier, amount, marking_codes: [...codes] } : { ...modifier, amount };
				modifiers.push(applySelectedSizeToModifier(withCodes, cardStore.stateCard?.selectedSize));
			}
		}
		return toSelectedGroup(group, modifiers);
	});
};

const closeSheet = () => {
	if (!cardStore.stateCard) return;
	cardStore.stateCard.isSectionModifier = false;
	cardStore.stateCard.groupCurrentOpen = null as never;
};

const handleApply = () => {
	const stateCard = cardStore.stateCard;
	if (!stateCard) return;

	const previousSelected = stateCard.groupModifiersSelected.map((group) => ({ ...group, modifiers: group.modifiers.map((modifier) => ({ ...modifier })) }));

	stateCard.groupModifiersSelected = syncStoreFromLocal();

	for (let i = 0; i < (stateCard.groupModifiers?.length ?? 0); i++) {
		const result = cardStore.validateGroup(i);
		if (!result.valid) {
			stateCard.groupModifiersSelected = previousSelected;
			hydrateLocalFromStore();
			const text = result.errorText ?? "";
			stateCard.textErrorRequaired = text;
			modulesStore.showNotification(text);
			scrollToRequiredEmpty();
			return;
		}
	}

	closeSheet();
	emit("apply");
};

provide(liquidModifiersKey, {
	localCounts,
	getKey,
	getAmount,
	getModifierPrice,
	selectModifier,
	incrementModifier,
	decrementModifier,
	removeModifier,
	canIncrementModifier,
	isSupportsModifierQuantity,
	shouldShowCounter,
	isHiddenModifier,
	isGroupSatisfied,
});

watch(
	[() => stateCard.value?.openCard?.id, () => (stateCard.value?.groupModifiers ?? []).map((group) => group.id).join("|")],
	([cardId]) => {
		_hasHydratedFromStore = false;
		if (!cardId) return;
		hydrateLocalFromStore();
		if (props.inline) collapseGroups();
	},
	{ immediate: true },
);

watch(
	() => stateCard.value?.isSectionModifier,
	(sectionOpen) => {
		if (sectionOpen) hydrateLocalFromStore();
	},
);

watch(localCounts, syncOnChange, { deep: true });
watch(() => stateCard.value?.selectedSize, syncOnChange, { deep: true });
watch([() => activeGroup.value?.id, windowWidth, windowHeight], fitInlineRows, { flush: "post" });

onMounted(() => {
	fitInlineRows();
	document.fonts?.ready.then(fitInlineRows);
});
</script>

<template>
	<div v-if="inline" ref="inlineRef" class="cms_inline">
		<LiquidModifiersGroup v-if="activeGroup" :key="activeGroup.id" :group="activeGroup" :rows="inlineRows" inline />
		<LiquidModifiersBar :groups="groups" :activeGroupId="activeGroup?.id ?? null" @select="toggleGroup" />
	</div>
	<LiquidModifiersPanel v-else-if="isPanel" :groups="groups" :open="isOpen" :price="price" :decoration="decoration" @close="closeSheet" @apply="handleApply" />
	<BottomSheet
		v-else
		v-model="isOpen"
		class="cms_root"
		:data-theme="themeStore.data?.kiosk_new_modifiers_theme?.toLowerCase()"
		:teleportTo="'body'"
		:maxScreenFraction="0.9"
		persistent
	>
		<template #header>
			<div class="cms_header">
				<p class="cms_header_title">{{ themeStore.data?.dish_cart_mod_choice_text || t(`product-card.modifiers.title`) }}</p>
				<button type="button" class="cms_close" @click="closeSheet">✕</button>
			</div>
		</template>

		<template #default>
			<LiquidModifiersGroup v-for="group in groups" :key="group.id" :group="group" />
		</template>

		<template #footer>
			<div class="cms_footer">
				<button type="button" class="cms_apply" @click="handleApply">
					{{ t(`card.button.apply`) }}
					<span v-if="totalExtra > 0"> (+ {{ formatPrice(totalExtra, mainStore.config_lib?.currency_symbol) }})</span>
				</button>
			</div>
		</template>
	</BottomSheet>
</template>

<style scoped lang="scss">
  @use './liquidModifiers.scss';
</style>
